import { AddressStateConst } from './address-state';
import { CandidatesConst } from './candidate';
import { CountryCodesConst } from './country-codes';
import { TaxonomyConst } from './taxonomy';

const { ProfileStatuses } = CandidatesConst;
const { CountryCodes, CountryCodesAbbreviations } = CountryCodesConst;
const { BusinessTitlesForLegacyJsApiUseOnly: BusinessTitles } = TaxonomyConst;

// Candidate ids are assigned by the server on create, so mockanator locates the
// hydrated ML recommendation candidates by this shared lastName instead.
export const TestMLRecommendedCandidateLastName = 'MLSuggested';

const series = (length: number, start?: number, step?: number) =>
  Array.from({ length }, (_, i) => (start || 0) + i * (step || 1));

// Deterministically spread valid states/provinces across generated talent by
// keying off the record's index, so each country's talent varies but the
// fixture stays reproducible.
const pickStateForIndex = (items: readonly string[], i = 0) =>
  items[i % items.length];

const tcObj = ({
  i,
  addressCountry,
  addressCountryCode,
  addressState,
  compensation,
  hourlyCompensation,
  desiredWeeklyMaxHours,
  maxHours,
  businessTitle,
  profileStatus,
}: {
  i?: number;
  addressCountry?: string;
  addressCountryCode?: string;
  addressState?: string;
  compensation?: number;
  desiredWeeklyMaxHours?: number;
  hourlyCompensation?: number;
  maxHours?: number;
  businessTitle?: string;
  profileStatus?: string;
} = {}) => ({
  firstName: `Extra${i}`,
  lastName: TestMLRecommendedCandidateLastName,
  profileStatus: profileStatus || ProfileStatuses.Waitlist,
  maxHours: maxHours ?? 50,
  desiredWeeklyMaxHours: desiredWeeklyMaxHours ?? 50,
  addressCountry: addressCountry || CountryCodes.US,
  addressCountryCode: addressCountryCode || CountryCodesAbbreviations.US,
  addressState: addressState || 'Washington',
  compensation: compensation || 100009.21,
  hourlyCompensation: hourlyCompensation || 102.31,
  businessTitle: businessTitle || BusinessTitles.Lawyer,
});

export const TestMLRecommendedCandidateData = [
  {
    firstName: 'Tony1hr',
    lastName: TestMLRecommendedCandidateLastName,
    profileStatus: ProfileStatuses.Waitlist,
    maxHours: 1,
    desiredWeeklyMaxHours: 1,
    addressCountryCode: CountryCodesAbbreviations.US,
    businessTitle: BusinessTitles.Lawyer,
  },
  {
    firstName: 'Clark5hrs',
    lastName: TestMLRecommendedCandidateLastName,
    profileStatus: ProfileStatuses.Waitlist,
    maxHours: 5.1,
    desiredWeeklyMaxHours: 5,
    addressCountryCode: CountryCodesAbbreviations.US,
    businessTitle: BusinessTitles.ContractsManager,
  },
  {
    firstName: 'Bruce30hrs',
    lastName: TestMLRecommendedCandidateLastName,
    profileStatus: ProfileStatuses.Waitlist,
    maxHours: 30,
    desiredWeeklyMaxHours: 30,
    addressCountryCode: CountryCodesAbbreviations.US,
    businessTitle: BusinessTitles.Investigator,
  },
  {
    firstName: 'Peter',
    lastName: TestMLRecommendedCandidateLastName,
    profileStatus: ProfileStatuses.Waitlist,
    maxHours: 40,
    desiredWeeklyMaxHours: 40,
    addressCountry: 'GB',
    addressCountryCode: 'GB',
    addressState: 'Northumberland',
    businessTitle: BusinessTitles.Lawyer,
  },
  tcObj({
    i: 5,
    profileStatus: ProfileStatuses.Alum,
    maxHours: 40,
    desiredWeeklyMaxHours: 40,
    businessTitle: BusinessTitles.Lawyer,
  }),
  tcObj({
    i: 6,
    businessTitle: BusinessTitles.LegalOperations,
    profileStatus: ProfileStatuses.Beach,
    maxHours: 40,
    desiredWeeklyMaxHours: 40,
  }),
  tcObj({
    i: 7,
    profileStatus: ProfileStatuses.Waitlist,
    maxHours: 40,
    desiredWeeklyMaxHours: 80,
  }),
  tcObj({
    i: 8,
    profileStatus: ProfileStatuses.Certifying,
    maxHours: 40,
    desiredWeeklyMaxHours: 40,
  }),
  tcObj({
    i: 9,
    businessTitle: BusinessTitles.LegalOperations,
    profileStatus: ProfileStatuses.InDiligence,
    maxHours: 40,
    desiredWeeklyMaxHours: 40,
  }),
  // some US
  ...series(5, 10).map(i =>
    tcObj({
      i,
      addressCountryCode: CountryCodesAbbreviations.US,
      businessTitle: BusinessTitles.Lawyer,
    })
  ),
  ...series(5, 15).map(i =>
    tcObj({
      i,
      addressCountryCode: CountryCodesAbbreviations.US,
      businessTitle: BusinessTitles.LegalOperations,
    })
  ),
  // some UK
  ...series(5, 20).map(i =>
    tcObj({
      i,
      addressCountry: CountryCodes.UK,
      addressCountryCode: CountryCodesAbbreviations.UK,
      addressState: 'Isles of Scilly',
      businessTitle: BusinessTitles.Lawyer,
    })
  ),
  ...series(5, 25).map(i =>
    tcObj({
      i,
      addressCountry: CountryCodes.UK,
      addressCountryCode: CountryCodesAbbreviations.UK,
      addressState: 'Isles of Scilly',
      businessTitle: BusinessTitles.LegalOperations,
    })
  ),
  // some low pay
  ...series(2, 30).map(i =>
    tcObj({
      i,
      compensation: 10000.21,
      hourlyCompensation: 4.8,
    })
  ),
  // some high pay
  ...series(3, 32).map(i =>
    tcObj({
      i,
      compensation: 500321.99,
      hourlyCompensation: 240.54,
    })
  ),
  // some Californians
  ...series(5, 35).map(i =>
    tcObj({
      i,
      addressState: 'California',
    })
  ),
  // some talent for country - AU
  ...series(5, 40).map(i =>
    tcObj({
      i,
      addressCountry: CountryCodes.AU,
      addressCountryCode: CountryCodesAbbreviations.AU,
      addressState: pickStateForIndex(
        AddressStateConst.australiaStatesAndTerritories,
        i
      ),
      businessTitle: BusinessTitles.Lawyer,
    })
  ),
  // some talent for country - CA
  ...series(5, 45).map(i =>
    tcObj({
      i,
      addressCountry: CountryCodes.CA,
      addressCountryCode: CountryCodesAbbreviations.CA,
      addressState: pickStateForIndex(
        AddressStateConst.canadaProvincesAndTerritories,
        i
      ),
      businessTitle: BusinessTitles.Lawyer,
    })
  ),
  // some talent for country - CH
  ...series(5, 50).map(i =>
    tcObj({
      i,
      addressCountry: CountryCodes.CH,
      addressCountryCode: CountryCodesAbbreviations.CH,
      businessTitle: BusinessTitles.Lawyer,
    })
  ),
  // some talent for country - DE
  ...series(5, 55).map(i =>
    tcObj({
      i,
      addressCountry: CountryCodes.DE,
      addressCountryCode: CountryCodesAbbreviations.DE,
      businessTitle: BusinessTitles.Lawyer,
    })
  ),
  // some talent for country - HK
  ...series(5, 60).map(i =>
    tcObj({
      i,
      addressCountry: CountryCodes.HK,
      addressCountryCode: CountryCodesAbbreviations.HK,
      businessTitle: BusinessTitles.Lawyer,
    })
  ),
  // some talent for country - SG
  ...series(5, 65).map(i =>
    tcObj({
      i,
      addressCountry: CountryCodes.SG,
      addressCountryCode: CountryCodesAbbreviations.SG,
      businessTitle: BusinessTitles.Lawyer,
    })
  ),
];
